import {useEffect, useRef} from "react";
import {useReactFlow, useStore, Node, Edge} from "@xyflow/react";
import {stratify, tree} from "d3-hierarchy";
import {FlowNodeTypes} from "@/types/node";

const DEFAULT_NODE_WIDTH = 340;
const DEFAULT_NODE_HEIGHT = 200;
const NODE_VERTICAL_SPACING = 56;

const layout = tree<Node>()
  .nodeSize([DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT])
  .separation(() => 1.5);

function layoutNodes(nodes: Node[], edges: Edge[]): Node[] {
  if (nodes.length === 0) return [];

  const workflowEdges = edges.filter((e: Edge) => e.type === "workflow");

  const hierarchy = stratify<Node>()
    .id(d => d.id)
    .parentId((d: Node) => workflowEdges.find(e => e.target === d.id)?.source)(
    nodes,
  );

  const root = layout(hierarchy);

  function computeNodePositions(
    node,
    parentY = 0,
    parentHeight = DEFAULT_NODE_HEIGHT,
  ) {
    const nodeHeight = node.data.measured?.height || DEFAULT_NODE_HEIGHT;

    const isNodePlaceholder = node.data.type === FlowNodeTypes.PLACEHOLDER;
    const verticalSpacing = isNodePlaceholder
      ? 2 * NODE_VERTICAL_SPACING
      : NODE_VERTICAL_SPACING;

    node.y = parentY + parentHeight + verticalSpacing;
    node.data.position.y = node.y;
    node.data.position.x = node.x;

    if (node.children) {
      node.children.forEach(child =>
        computeNodePositions(child, node.data.position.y, nodeHeight),
      );
    }
  }

  computeNodePositions(root, 0, DEFAULT_NODE_HEIGHT);

  return root.descendants().map(d => ({...d.data, position: {x: d.x, y: d.y}}));
}

function useLayout() {
  // Recalculate layout when nodes are added or removed and when measured data changes
  const {getNodes, getEdges, setNodes} = useReactFlow();
  const nodes = useStore(state => state.nodes);
  const nodeCount = useStore(state => state.nodeLookup.size);

  const prevMeasuredRef = useRef<
    Record<string, {height: number; width: number}>
  >({});

  useEffect(() => {
    const currentNodes = getNodes();
    const edges = getEdges();

    const newMeasured: Record<string, {height: number; width: number}> = {};
    let hasChanges = false;

    for (const node of currentNodes) {
      const measured = (node as any)?.measured || {};
      const height = measured.height || DEFAULT_NODE_HEIGHT;
      const width = measured.width || DEFAULT_NODE_WIDTH;

      const prev = prevMeasuredRef.current[node.id];
      newMeasured[node.id] = {height, width};

      if (!prev || prev.height !== height || prev.width !== width) {
        hasChanges = true;
      }
    }

    const prevIds = Object.keys(prevMeasuredRef.current);
    const newIds = Object.keys(newMeasured);
    if (
      prevIds.length !== newIds.length ||
      prevIds.some(id => !newMeasured[id])
    ) {
      hasChanges = true;
    }

    if (hasChanges) {
      const targetNodes = layoutNodes(currentNodes, edges);
      setNodes(targetNodes);
      prevMeasuredRef.current = newMeasured;
    }
  }, [nodes, nodeCount]);
}

export default useLayout;
